import type { Requisition } from '../types';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { Copy03 } from '@kula-ai/icons/outline/Copy03';
import { EyeOff } from '@kula-ai/icons/outline/EyeOff';
import { Eye } from '@kula-ai/icons/outline/Eye';
// import { CheckCircle } from '@kula-ai/icons/outline/CheckCircle';
// import { RotateClockwise01 } from '@kula-ai/icons/outline/RotateClockwise01';
import { Archive } from '@kula-ai/icons/outline/Archive';
import { Trash03 } from '@kula-ai/icons/dual-tone/Trash03';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';

export type RequisitionActionType =
  | 'edit'
  | 'clone'
  | 'make_confidential'
  | 'make_public'
  | 'close'
  | 'close_single'
  | 'open'
  | 'reopen'
  | 'archive';

export interface RequisitionAction {
  id: RequisitionActionType;
  label: string;
  description?: string; // Description text for the action
  icon?: any; // Icon component
  color?: string; // For destructive actions
  dividerAfter?: boolean; // Add divider after this action
}

// All possible actions
export const REQUISITION_ACTIONS: Record<
  RequisitionActionType,
  RequisitionAction
> = {
  edit: {
    id: 'edit',
    label: 'Edit requisition',
    // description: 'Make changes to this requisition.',
    icon: Edit05,
  },
  clone: {
    id: 'clone',
    label: 'Clone requisition',
    // description: 'Create a copy of this requisition.',
    icon: Copy03,
  },
  make_confidential: {
    id: 'make_confidential',
    label: 'Make confidential',
    // description: 'Hide this requisition from general view.',
    icon: EyeOff,
  },
  make_public: {
    id: 'make_public',
    label: 'Make non confidential',
    // description: 'Make this requisition visible to everyone.',
    icon: Eye,
  },
  close: {
    id: 'close',
    label: 'Close requisition',
    description:
      'Mark this requisition as closed. Closed requisitions can be reopened or archived',
    icon: Trash03,
    color: 'error-200',
    dividerAfter: true,
  },
  close_single: {
    id: 'close_single',
    label: 'Close this requisition alone',
    description:
      'Mark this requisition as closed. Closed requisitions can be reopened or archived.',
    icon: Trash03,
    color: 'error-200',
  },
  open: {
    id: 'open',
    label: 'Open requisition',
    description: 'Mark this requisition as open.',
    icon: PlusCircle,
    // color: 'primary-500',
  },
  reopen: {
    id: 'reopen',
    label: 'Reopen requisition',
    description: 'Reopen this closed requisition',
    // icon: RotateClockwise01,
  },
  archive: {
    id: 'archive',
    label: 'Archive requisition',
    description: 'Move this requisition to archived status',
    icon: Archive,
    color: 'error-200',
  },
};

// Group action configurations - separate copy for group actions
export const REQUISITION_GROUP_ACTIONS: Record<
  RequisitionActionType,
  {
    singleLabel: string;
    groupLabel: string;
    singleDescription?: string;
    groupDescription?: string;
  }
> = {
  open: {
    singleLabel: 'Open this requisition alone',
    groupLabel: 'Open all requisitions in the group',
  },
  close: {
    singleLabel: 'Close this requisition alone',
    groupLabel: 'Close all requisitions in the group',
    singleDescription:
      'Mark this requisition as closed. Closed requisitions can be reopened or archived.',
  },
  close_single: {
    singleLabel: 'Close this requisition alone',
    groupLabel: 'Close this requisition alone',
    singleDescription:
      'Mark this requisition as closed. Closed requisitions can be reopened or archived.',
  },
  reopen: {
    singleLabel: 'Reopen this requisition',
    groupLabel: 'Reopen all requisitions in the group',
  },
  archive: {
    singleLabel: 'Archive this requisition',
    groupLabel: 'Archive all requisitions in the group',
    singleDescription: 'Move this requisition to archived status',
    groupDescription:
      'Move this and all requisitions in the group to archived status',
  },
  make_confidential: {
    singleLabel: 'Make this group confidential',
    groupLabel: 'Make this group confidential',
  },
  make_public: {
    singleLabel: 'Make this group non confidential',
    groupLabel: 'Make this group non confidential',
  },
  edit: {
    singleLabel: 'Edit this requisition alone',
    groupLabel: 'Edit all requisitions in the group',
    singleDescription:
      'Editing this requisition alone will detach it from the group and assign a new requisition ID to it',
  },
  clone: {
    singleLabel: 'Clone requisition',
    groupLabel: 'Clone requisition',
    singleDescription: 'Create a copy of this requisition.',
    groupDescription: 'Create a copy of this requisition.',
  },
};

// State-based action mapping
export const REQUISITION_ACTIONS_BY_STATUS: Record<
  Requisition['status'],
  RequisitionActionType[]
> = {
  open: ['edit', 'clone', 'make_confidential', 'close'],

  approved: ['open', 'edit', 'clone', 'make_confidential', 'close'],

  draft: ['edit', 'clone', 'make_confidential', 'close'],

  pending_approval: ['edit', 'clone', 'make_confidential', 'close'],

  rejected: ['edit', 'clone', 'make_confidential', 'close'],

  filled: ['clone', 'close'],

  closed: ['reopen', 'edit', 'clone', 'archive'],

  archived: ['clone'],

  // Fallback for other statuses
  created: ['open', 'edit', 'clone', 'make_confidential', 'close'],
};

/**
 * Get actions for a requisition based on its status and confidential flag
 *
 * @param requisition - The requisition to get actions for
 * @param options - Additional options
 * @param options.excludeViewDetails - Exclude "View details" action (useful for cards that navigate on click)
 * @returns Array of actions available for this requisition
 */
export const getRequisitionActions = (
  requisition: Requisition,
  options?: {
    excludeViewDetails?: boolean;
  }
): RequisitionAction[] => {
  const { status, confidential, requisition_association } = requisition;
  const actionIds = REQUISITION_ACTIONS_BY_STATUS[status] || [];

  let actions = actionIds.map((id) => REQUISITION_ACTIONS[id]).filter(Boolean);

  // Toggle between make_confidential and make_public based on current status
  if (confidential) {
    // If already confidential, show "Make public" instead
    actions = actions.map((action) =>
      action.id === 'make_confidential'
        ? REQUISITION_ACTIONS.make_public
        : action
    );
  }

  // Remove reopen if requisition has an association (was filled)
  if (requisition_association?.id) {
    actions = actions.filter((action) => action.id !== 'reopen');
  }

  return actions;
};
